<script setup lang="ts">
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import { computed, shallowRef, watch } from 'vue';
import logger from '@/modules/common/services/logger.service';
import axios, { AxiosError } from 'axios';
import { AuctionSecurity, RawAuctionSecurity } from '@/modules/common/models';
import { AgTableServer } from '@/modules/common/components/ag-table';
import { EventReadyConfig } from '@/modules/common/components/ag-table/AgTableServer.vue';
import * as cols from '@/modules/common/components/ag-table/columns/broker-securities';
import { ColDef, LoadSuccessParams, SortModelItem } from 'ag-grid-enterprise';
import i18n from '@/localisation/i18n';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { ApiError } from '@/utils/errors/api-error';

type SecurityFilterValue = 'all' | 'occ' | 'nscc' | 'marketplace';
type SecurityFilters = Array<{ text: string; value: SecurityFilterValue }>;

const pageSize = shallowRef(100);

const dialog = useDialogManager();
const snackbar = useSnackbarManager();

const securityFilters: SecurityFilters = [
  { text: i18n.tc('equities.filter.all'), value: 'all' },
  { text: i18n.tc('equities.filter.occEligible'), value: 'occ' },
  { text: i18n.tc('equities.filter.nsccEligible'), value: 'nscc' },
  { text: i18n.tc('equities.filter.marketplaceEligible'), value: 'marketplace' },
];

const securitySearch = shallowRef('');
const securityFilter = shallowRef<SecurityFilterValue>('all');
const page = shallowRef(1);
const sort = shallowRef<SortModelItem>({ colId: 'ticker', sort: 'asc' });

let tableApi: EventReadyConfig | null = null;

function getRowId(item: AuctionSecurity): string {
  return item.id;
}

const columnDefs = computed(
  (): Array<ColDef<AuctionSecurity>> => [
    cols.enabled(onToggleEnabled),
    cols.restricted(onToggleRestricted),
    cols.ticker(),
    cols.cusip(),
    cols.isinCode(),
    cols.security(),
    cols.securityCategory(),
    cols.securityType(),
    cols.securitySource(),
    cols.lastClosePrice(),
    cols.bondYield(),
    cols.marketplaceEligible(),
    cols.tradeManagementEligible(),
    cols.nsccNewEligible(),
    cols.nsccRollEligible(),
    cols.occEligible(),
  ]
);

// Map frontend column IDs to backend SQL column names
const sqlColumnMap: Record<string, string> = {
  security: 'name',
  isinCode: 'isin',
  securitySource: 'source',
  yield: 'yield',
  securityType: 'security_type',
  securityCategory: 'category',
  lastClosePrice: 'last_close_price',
};

const queryConfig = computed(() => ({
  filter: securityFilter.value,
  query: securitySearch.value ? securitySearch.value.toUpperCase() : '',
}));

// Refresh table when search/filter changes
watch(queryConfig, () => tableApi?.refresh({ purge: true }), { deep: true });

async function queryData(config: {
  page: number;
  pageSize: number;
  sort: SortModelItem;
  signal: AbortSignal;
}): Promise<LoadSuccessParams | undefined> {
  const sortField = sqlColumnMap[config.sort.colId] ?? config.sort.colId;
  const sortPrefix = config.sort.sort === 'desc' ? '-' : '+';

  const params = {
    ...queryConfig.value,
    sort: `${sortPrefix}${sortField}`,
    page: config.page,
    limit: config.pageSize,
  };

  try {
    const { data } = await axios.get<{
      status: string;
      equities: RawAuctionSecurity[];
      recordCount: number;
    }>('/api/1/broker-admin/equities', {
      params,
      signal: config.signal,
    });

    return {
      rowData: data.equities.map<AuctionSecurity>(AuctionSecurity.fromData),
      rowCount: data.recordCount,
    };
  } catch (err) {
    if (axios.isCancel(err)) {
      return undefined;
    }
    logger.warn(err);
    return { rowData: [], rowCount: 0 };
  }
}

function onTableReady(config: EventReadyConfig): void {
  tableApi = config;
}

// API functions
async function enableSecurity(security: AuctionSecurity): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/equities/${security.id}/enable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${security.ticker} [${security.cusip}] cannot be enabled`)
    );
  }
}

async function disableSecurity(security: AuctionSecurity): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/equities/${security.id}/disable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(
        e as AxiosError,
        `${security.ticker} [${security.cusip}] cannot be disabled`
      )
    );
  }
}

async function restrictSecurity(security: AuctionSecurity): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/equities/${security.id}/restrict`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(
        e as AxiosError,
        `${security.ticker} [${security.cusip}] cannot be restricted`
      )
    );
  }
}

async function unrestrictSecurity(security: AuctionSecurity): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/equities/${security.id}/unrestrict`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(
        e as AxiosError,
        `${security.ticker} [${security.cusip}] cannot remove restriction`
      )
    );
  }
}

function askConfirmation(
  title: string,
  message: string,
  security: AuctionSecurity,
  accept: () => void,
  reject: () => void
): void {
  dialog.ask({
    title: i18n.t(title) as string,
    color: 'warning',
    icon: 'mdi-check',
    message: i18n.t(message, { ticker: security.ticker }),
    acceptText: i18n.tc('dialogs.submitButton'),
    onAccept: () => accept(),
    rejectText: i18n.tc('dialogs.cancelButton'),
    onReject: () => reject(),
    shouldManuallyConfirm: true,
  });
}

function onToggleEnabled(security: AuctionSecurity): void {
  try {
    if (security.isAuroraActive) {
      askConfirmation(
        'equities.enable.title',
        'equities.enable.message',
        security,
        async () => {
          await enableSecurity(security);
          tableApi?.refresh({ purge: true });
          snackbar.confirm(`${security.ticker} [${security.cusip}] has been enabled.`);
        },
        () => {
          security.isAuroraActive = false;
          tableApi?.redrawRows();
        }
      );
    } else {
      askConfirmation(
        'equities.disable.title',
        'equities.disable.message',
        security,
        async () => {
          await disableSecurity(security);
          tableApi?.refresh({ purge: true });
          snackbar.confirm(`${security.ticker} [${security.cusip}] has been disabled.`);
        },
        () => {
          security.isAuroraActive = true;
          tableApi?.redrawRows();
        }
      );
    }
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

function onToggleRestricted(security: AuctionSecurity): void {
  try {
    if (security.isAuroraRestricted) {
      askConfirmation(
        'equities.restrict.title',
        'equities.restrict.message',
        security,
        async () => {
          await restrictSecurity(security);
          tableApi?.refresh({ purge: true });
          snackbar.confirm(`${security.ticker} [${security.cusip}] trading is now restricted.`);
        },
        () => {
          security.isAuroraRestricted = false;
          tableApi?.redrawRows();
        }
      );
    } else {
      askConfirmation(
        'equities.unrestrict.title',
        'equities.unrestrict.message',
        security,
        async () => {
          await unrestrictSecurity(security);
          tableApi?.refresh({ purge: true });
          snackbar.confirm(`${security.ticker} [${security.cusip}] trading is now unrestricted.`);
        },
        () => {
          security.isAuroraRestricted = true;
          tableApi?.redrawRows();
        }
      );
    }
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

function resetPage(): void {
  page.value = 1;
}
</script>

<template>
  <DashboardPanel no-collapse title="Securities">
    <VAlert
      border="top"
      class="ma-4 py-6 securities-intro-alert"
      color="primary"
      colored-border
      elevation="8"
      type="info"
    >
      <span class="header">Select which Securities are eligible for trading.</span><br />
      <span class="sub">
        For a security to be actively traded it needs to be marked as Enabled (by us), and it should
        be NSCC New and Roll Eligible and Marketplace Eligible. <br />
        For a security to be marketplace eligible, the ticker must not end in 'XXXX', last close
        price should be larger than 0.05 and it should have a valid polygon type (CS, ETF, FUND,
        PFD, ADRC, ETS, SP, OS, ETV, GDR).</span
      >
    </VAlert>
    <VContainer fluid>
      <VRow class="mb-4">
        <VCol>
          <VTextField
            v-model="securitySearch"
            autofocus
            clearable
            :label="$t('searchEquities')"
            prepend-inner-icon="mdi-magnify"
            @input="resetPage"
          ></VTextField>
        </VCol>
        <VCol>
          <VSelect
            v-model="securityFilter"
            :items="securityFilters"
            :label="$t('equities.filter.label')"
            @change="resetPage"
          ></VSelect>
        </VCol>
      </VRow>
    </VContainer>
    <AgTableServer
      :column-defs="columnDefs"
      :get-row-id="getRowId"
      :page="page"
      :page-size="pageSize"
      :query-data="queryData"
      :sort="sort"
      @ready="onTableReady"
      @update:page="page = $event"
      @update:page-size="pageSize = $event"
      @update:sort="sort = $event"
    />
  </DashboardPanel>
</template>

<style lang="scss" scoped>
.securities-intro-alert {
  .sub {
    color: #7a7a7a;
    font-size: 0.9em;
  }
}

:deep(.v-input--selection-controls) {
  margin: 0;
  padding: 0;
}
</style>
